import { Link } from 'react-router-dom'
import { useQuery } from 'react-query'
import { challengesAPI } from '../services/api'
import { 
  TrophyIcon, 
  UserGroupIcon, 
  GlobeAltIcon, 
  LightBulbIcon,
  ArrowRightIcon,
  PlayIcon,
  RocketLaunchIcon,
  AcademicCapIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import ImageWithFallback from '../components/ui/ImageWithFallback'
import { getCategoryGradient, getPlaceholderImage, getLocalImage, heroImages } from '../utils/images'

const features = [
  {
    name: 'Global Competition',
    description: 'Compete with researchers, students, and corporations from around the world in AI challenges.',
    icon: GlobeAltIcon,
    image: getLocalImage('globalcompetition'),
  },
  {
    name: 'Real-world Problems',
    description: 'Solve real-world challenges and build innovative AI solutions that make a difference.',
    icon: LightBulbIcon,
    image: getLocalImage('realworldproblems'),
  },
  {
    name: 'Expert Community',
    description: 'Connect with industry experts and fellow AI enthusiasts in our vibrant community.',
    icon: UserGroupIcon,
    image: getLocalImage('experts'),
  },
  {
    name: 'Prestigious Awards',
    description: 'Win substantial prizes and gain recognition for your innovative AI solutions.',
    icon: TrophyIcon,
    image: getLocalImage('awards'),
  },
]

const stats = [
  { id: 1, name: 'Global Participants', value: '10,000+', icon: UserGroupIcon },
  { id: 2, name: 'Countries Represented', value: '50+', icon: GlobeAltIcon },
  { id: 3, name: 'Total Prize Pool', value: '$500K+', icon: TrophyIcon },
  { id: 4, name: 'AI Solutions Submitted', value: '2,500+', icon: ChartBarIcon },
]

const testimonials = [
  {
    content: "ZovaLabs pushed me to think beyond traditional approaches. The innovative platform and expert community helped me grow as an AI researcher.",
    author: "Dr. Sarah Chen",
    role: "AI Research Scientist",
    company: "MIT",
    avatar: getLocalImage('experts'),
  },
  {
    content: "Participating in these challenges has been instrumental in advancing our startup's AI capabilities. The community and resources are invaluable.",
    author: "Marcus Rodriguez",
    role: "CTO",
    company: "BuildTech AI",
    avatar: getLocalImage('experts'),
  },
  {
    content: "As a student, this platform gave me the opportunity to work on cutting-edge problems and connect with industry leaders. It's been transformative.",
    author: "Priya Patel",
    role: "PhD Student",
    company: "Stanford University",
    avatar: getLocalImage('experts'),
  },
]

export default function HomePage() {
  const { data: challengesData, isLoading } = useQuery(
    'featured-challenges',
    () => challengesAPI.getAll({ status: 'active' }),
    {
      select: (response) => response.data.challenges.slice(0, 3)
    }
  )

  return (
    <div className="bg-dark-900 overflow-hidden">
      {/* Revolutionary Hero Section */}
      <div className="relative min-h-screen aurora-bg particle-bg overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 golden-mesh opacity-30"></div>
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-gold-400 rounded-full animate-floating-dots"></div>
          <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-gold-500 rounded-full animate-sparkle" style={{animationDelay: '1s'}}></div>
          <div className="absolute bottom-1/4 left-1/3 w-3 h-3 bg-gold-300 rounded-full animate-pulse" style={{animationDelay: '2s'}}></div>
        </div>
        
        {/* Main Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between min-h-screen py-20">
            
            {/* Left Content */}
            <div className="lg:w-1/2 text-center lg:text-left">
              <div className="space-y-8">
                {/* Artistic Badge */}
                <div className="inline-flex items-center glass-morphism px-6 py-3 rounded-full">
                  <ImageWithFallback
                    src="/images/zova_labs_right.png"
                    alt="ZovaLabs Logo"
                    className="w-5 h-5 mr-2 object-contain"
                  />
                  <span className="text-sm font-medium text-gray-300">AI Innovation Platform</span>
                  <div className="ml-2 w-2 h-2 bg-gold-400 rounded-full animate-pulse"></div>
                </div>

                {/* Main Heading with Holographic Effect */}
                <h1 className="text-5xl sm:text-6xl lg:text-7xl font-display font-bold leading-tight">
                  <span className="block text-white mb-2">
                    Welcome to
                    <span className="inline-block ml-4 w-12 h-1 bg-gradient-gold animate-gold-shimmer"></span>
                  </span>
                  <span className="block holographic-text animate-holographic-shift text-stroke-gold">
                    ZovaLabs
                  </span>
                  <span className="block text-2xl sm:text-3xl lg:text-4xl text-gray-300 mt-4 font-normal">
                    Where Innovation Meets Intelligence
                  </span>
                </h1>

                {/* Enhanced Description */}
                <p className="text-xl text-gray-300 max-w-2xl leading-relaxed">
                  Empowering the next generation of AI innovators. Connect with researchers, developers, 
                  and industry leaders to build groundbreaking solutions that shape the future.
                </p>

                {/* Action Buttons with New Design */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Link
                    to="/challenges"
                    className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-gradient-gold rounded-xl shadow-2xl hover:shadow-gold transition-all duration-300 transform hover:scale-105 artistic-border"
                  >
                    <span className="relative z-10 flex items-center">
                      Explore Challenges
                      <ArrowRightIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 rounded-xl"></div>
                  </Link>
                  
                  <button className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-gold-400 glass-morphism rounded-xl hover:bg-gold-500/10 transition-all duration-300">
                    <PlayIcon className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                    Watch Demo
                    <div className="absolute inset-0 rounded-xl bg-gradient-gold opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                  </button>
                </div>

                {/* Trust Indicators */}
                <div className="flex items-center space-x-8 pt-8">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gold-400">10K+</div>
                    <div className="text-sm text-gray-400">Participants</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gold-400">50+</div>
                    <div className="text-sm text-gray-400">Countries</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gold-400">$500K+</div>
                    <div className="text-sm text-gray-400">Prize Pool</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Visual Element */}
            <div className="lg:w-1/2 mt-16 lg:mt-0 relative">
              <div className="relative max-w-lg mx-auto">
                {/* Main Visual Container */}
                <div className="relative">
                  <div className="relative">


                    {/* Feature Cards */}
                    <div className="space-y-4">
                      {[
                        { icon: GlobeAltIcon, text: "Global Competition", delay: "0s" },
                        { icon: LightBulbIcon, text: "Real-world Problems", delay: "0.5s" },
                        { icon: TrophyIcon, text: "Prestigious Awards", delay: "1s" }
                      ].map((item, index) => (
                        <div 
                          key={index}
                          className="flex items-center space-x-4 glass-morphism p-4 rounded-xl hover:bg-gold-500/5 transition-all duration-300 group"
                          style={{animationDelay: item.delay}}
                        >
                          <div className="w-10 h-10 bg-gradient-gold rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                            <item.icon className="w-5 h-5 text-dark-900" />
                          </div>
                          <span className="text-gray-300 font-medium group-hover:text-gold-400 transition-colors duration-200">
                            {item.text}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Floating Decorative Elements */}
                <div className="absolute -top-4 -left-4 w-8 h-8 bg-gold-400/20 rounded-full animate-float"></div>
                <div className="absolute -bottom-4 -right-4 w-6 h-6 bg-gold-500/30 rounded-full animate-pulse" style={{animationDelay: '2s'}}></div>
                <div className="absolute top-1/2 -right-8 w-4 h-4 bg-gold-300/40 rounded-full animate-sparkle" style={{animationDelay: '1.5s'}}></div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-gold-400 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-gold-400 rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </div>

      {/* Revolutionary Stats Section */}
      <div className="relative py-24 aurora-bg">
        <div className="absolute inset-0 golden-mesh opacity-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center glass-morphism px-6 py-3 rounded-full mb-6">
              <TrophyIcon className="w-5 h-5 text-gold-400 mr-2 animate-sparkle" />
              <span className="text-sm font-medium text-gray-300">Global Impact</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-display font-bold text-white mb-6">
              Trusted by innovators
              <span className="block holographic-text animate-holographic-shift">worldwide</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Join thousands of participants making a difference in AI and building technology
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div 
                key={stat.id} 
                className="group relative"
                style={{animationDelay: `${index * 0.2}s`}}
              >
                {/* Stat Card */}
                <div className="luxury-card p-8 rounded-2xl text-center hover:scale-105 transition-all duration-300 perspective-card gradient-border-mask">
                  
                  {/* Icon Container */}
                  <div className="relative mb-6">
                    <div className="w-16 h-16 mx-auto neo-morphism rounded-2xl flex items-center justify-center group-hover:animate-gold-pulse">
                      <stat.icon className="w-8 h-8 text-gold-400 group-hover:scale-110 transition-transform duration-200" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-4 h-4 bg-gold-400 rounded-full animate-sparkle" style={{animationDelay: `${index * 0.3}s`}}></div>
                  </div>

                  {/* Stat Value */}
                  <div className="mb-4">
                    <div className="text-4xl lg:text-5xl font-bold holographic-text animate-holographic-shift">
                      {stat.value}
                    </div>
                  </div>

                  {/* Stat Label */}
                  <div className="text-gray-300 font-medium group-hover:text-gold-400 transition-colors duration-200">
                    {stat.name}
                  </div>

                  {/* Decorative Elements */}
                  <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-12 h-0.5 bg-gradient-gold opacity-60 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>

                {/* Floating Decorative Dots */}
                <div className="absolute -top-2 -left-2 w-3 h-3 bg-gold-400/30 rounded-full animate-float" style={{animationDelay: `${index * 0.5}s`}}></div>
                <div className="absolute -bottom-2 -right-2 w-2 h-2 bg-gold-500/40 rounded-full animate-pulse" style={{animationDelay: `${index * 0.7}s`}}></div>
              </div>
            ))}
          </div>

          {/* Bottom Decorative Element */}
          <div className="mt-16 text-center">
            <div className="inline-flex items-center space-x-2">
              <div className="w-2 h-2 bg-gold-400 rounded-full animate-pulse"></div>
              <div className="w-16 h-0.5 bg-gradient-gold"></div>
              <ImageWithFallback
                src="/images/zova_labs_right.png"
                alt="ZovaLabs Logo"
                className="w-6 h-6 object-contain"
              />
              <div className="w-16 h-0.5 bg-gradient-gold"></div>
              <div className="w-2 h-2 bg-gold-400 rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
            </div>
          </div>
        </div>
      </div>

      {/* Artistic Features Section */}
      <div className="relative py-24 bg-dark-900 particle-bg">
        <div className="absolute inset-0 golden-mesh opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Section Header */}
          <div className="text-center mb-20">
            <div className="inline-flex items-center glass-morphism px-6 py-3 rounded-full mb-6">
              <LightBulbIcon className="w-5 h-5 text-gold-400 mr-2 animate-sparkle" />
              <span className="text-sm font-medium text-gray-300">Why Participate</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-display font-bold text-white mb-6">
              A better way to
              <span className="block holographic-text animate-holographic-shift">innovate with AI</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Join the global community of AI innovators working on real-world challenges in building technology
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            {features.map((feature, index) => (
              <div 
                key={feature.name} 
                className="group relative"
                style={{animationDelay: `${index * 0.2}s`}}
              >
                {/* Feature Card */}
                <div className="luxury-card rounded-3xl overflow-hidden hover:scale-105 transition-all duration-500 perspective-card floating-elements">
                  
                  {/* Image Section */}
                  <div className="relative h-64 overflow-hidden">
                    <ImageWithFallback
                      src={feature.image}
                      alt={feature.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      fallbackClassName="bg-gradient-to-br from-dark-700 to-dark-800"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-dark-900/50 to-transparent" />
                    
                    {/* Floating Icon */}
                    <div className="absolute top-6 right-6">
                      <div className="w-12 h-12 neo-morphism rounded-xl flex items-center justify-center group-hover:animate-gold-pulse">
                        <feature.icon className="w-6 h-6 text-gold-400 group-hover:scale-110 transition-transform duration-200" />
                      </div>
                    </div>

                    {/* Decorative Elements */}
                    <div className="absolute top-4 left-4 w-3 h-3 bg-gold-400 rounded-full animate-sparkle" style={{animationDelay: `${index * 0.5}s`}}></div>
                    <div className="absolute bottom-4 right-4 w-2 h-2 bg-gold-500 rounded-full animate-pulse" style={{animationDelay: `${index * 0.7}s`}}></div>
                  </div>

                  {/* Content Section */}
                  <div className="p-8 relative">
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-gold opacity-60"></div>
                    
                    <h3 className="text-2xl font-display font-bold text-white mb-4 group-hover:holographic-text group-hover:animate-holographic-shift transition-all duration-300">
                      {feature.name}
                    </h3>
                    
                    <p className="text-gray-300 leading-relaxed group-hover:text-gray-200 transition-colors duration-300">
                      {feature.description}
                    </p>

                    {/* Action Indicator */}
                    <div className="mt-6 flex items-center text-gold-400 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                      <span className="text-sm font-medium">Learn more</span>
                      <ArrowRightIcon className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
                    </div>

                    {/* Bottom Decorative Line */}
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-gold opacity-0 group-hover:opacity-60 transition-opacity duration-300"></div>
                  </div>
                </div>

                {/* Floating Decorative Elements */}
                <div className="absolute -top-3 -left-3 w-6 h-6 bg-gold-400/20 rounded-full animate-float" style={{animationDelay: `${index * 0.8}s`}}></div>
                <div className="absolute -bottom-3 -right-3 w-4 h-4 bg-gold-500/30 rounded-full animate-pulse" style={{animationDelay: `${index * 1.2}s`}}></div>
              </div>
            ))}
          </div>

          {/* Bottom Artistic Element */}
          <div className="mt-20 text-center">
            <div className="inline-flex items-center space-x-4">
              <div className="w-8 h-0.5 bg-gradient-gold animate-gold-shimmer"></div>
              <div className="w-3 h-3 bg-gold-400 rounded-full animate-sparkle"></div>
              <div className="w-16 h-0.5 bg-gradient-gold animate-gold-shimmer" style={{animationDelay: '0.5s'}}></div>
              <div className="w-3 h-3 bg-gold-500 rounded-full animate-pulse"></div>
              <div className="w-8 h-0.5 bg-gradient-gold animate-gold-shimmer" style={{animationDelay: '1s'}}></div>
            </div>
          </div>
        </div>
      </div>

      {/* Revolutionary Featured Challenges Section */}
      <div className="relative py-24 aurora-bg">
        <div className="absolute inset-0 golden-mesh opacity-15"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Section Header */}
          <div className="text-center mb-20">
            <div className="inline-flex items-center glass-morphism px-6 py-3 rounded-full mb-6">
              <RocketLaunchIcon className="w-5 h-5 text-gold-400 mr-2 animate-sparkle" />
              <span className="text-sm font-medium text-gray-300">Current Challenges</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-display font-bold text-white mb-6">
              Featured
              <span className="block holographic-text animate-holographic-shift">Challenges</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Explore our current AI challenges and start building innovative solutions
            </p>
          </div>

          {/* Challenges Content */}
          {isLoading ? (
            <div className="flex justify-center">
              <div className="luxury-card p-12 rounded-3xl">
                <LoadingSpinner size="lg" />
                <p className="mt-4 text-gray-300">Loading challenges...</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {challengesData?.map((challenge, index) => (
                <div 
                  key={challenge.id} 
                  className="group relative"
                  style={{animationDelay: `${index * 0.2}s`}}
                >
                  {/* Challenge Card */}
                  <div className="luxury-card rounded-3xl overflow-hidden hover:scale-105 transition-all duration-500 perspective-card floating-elements">
                    
                    {/* Image Section */}
                    <div className="relative h-56 overflow-hidden">
                      <ImageWithFallback
                        src={getPlaceholderImage(400, 200, `${challenge.category.toLowerCase().replace(' ', '-')},ai,technology`)}
                        alt={challenge.category}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        fallbackSrc={`data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 200'%3E%3Crect width='400' height='200' fill='%23334155'/%3E%3Ctext x='200' y='100' text-anchor='middle' dy='.3em' fill='%23f59e0b' font-family='Arial, sans-serif' font-size='16'%3E${challenge.category}%3C/text%3E%3C/svg%3E`}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-dark-900/50 to-transparent" />
                      
                      {/* Badges */}
                      <div className="absolute top-4 left-4">
                        <div className="glass-morphism px-3 py-1 rounded-full">
                          <span className="text-xs font-medium text-gold-400">{challenge.category}</span>
                        </div>
                      </div>
                      <div className="absolute top-4 right-4">
                        <div className={`glass-morphism px-3 py-1 rounded-full ${
                          challenge.difficulty === 'beginner' ? 'border-green-500/30' :
                          challenge.difficulty === 'intermediate' ? 'border-yellow-500/30' :
                          'border-red-500/30'
                        }`}>
                          <span className={`text-xs font-medium ${
                            challenge.difficulty === 'beginner' ? 'text-green-400' :
                            challenge.difficulty === 'intermediate' ? 'text-yellow-400' :
                            'text-red-400'
                          }`}>
                            {challenge.difficulty}
                          </span>
                        </div>
                      </div>

                      {/* Decorative Elements */}
                      <div className="absolute bottom-4 right-4 w-3 h-3 bg-gold-400 rounded-full animate-sparkle" style={{animationDelay: `${index * 0.4}s`}}></div>
                    </div>

                    {/* Content Section */}
                    <div className="p-6 relative">
                      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-gold opacity-60"></div>
                      
                      {/* Title */}
                      <Link to={`/challenges/${challenge.id}`} className="block group/link">
                        <h3 className="text-xl font-display font-bold text-white mb-3 group-hover/link:holographic-text group-hover/link:animate-holographic-shift transition-all duration-300">
                          {challenge.title}
                        </h3>
                      </Link>
                      
                      {/* Description */}
                      <p className="text-gray-300 text-sm leading-relaxed mb-6 line-clamp-3 group-hover:text-gray-200 transition-colors duration-300">
                        {challenge.description}
                      </p>

                      {/* Footer */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-gray-400 group-hover:text-gold-400 transition-colors duration-200">
                          <UserGroupIcon className="h-4 w-4 mr-2" />
                          <span className="text-sm font-medium">
                            {challenge.participant_count} participants
                          </span>
                        </div>
                        
                        <Link
                          to={`/challenges/${challenge.id}`}
                          className="flex items-center text-gold-400 hover:text-gold-300 font-medium text-sm group/cta"
                        >
                          Learn more
                          <ArrowRightIcon className="ml-1 h-4 w-4 group-hover/cta:translate-x-1 transition-transform duration-200" />
                        </Link>
                      </div>

                      {/* Bottom Decorative Line */}
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-gold opacity-0 group-hover:opacity-60 transition-opacity duration-300"></div>
                    </div>
                  </div>

                  {/* Floating Decorative Elements */}
                  <div className="absolute -top-2 -left-2 w-4 h-4 bg-gold-400/20 rounded-full animate-float" style={{animationDelay: `${index * 0.6}s`}}></div>
                  <div className="absolute -bottom-2 -right-2 w-3 h-3 bg-gold-500/30 rounded-full animate-pulse" style={{animationDelay: `${index * 0.8}s`}}></div>
                </div>
              ))}
            </div>
          )}

          {/* Call to Action */}
          <div className="mt-16 text-center">
            <Link
              to="/challenges"
              className="group relative inline-flex items-center justify-center px-10 py-4 text-lg font-semibold text-white bg-gradient-gold rounded-xl shadow-2xl hover:shadow-gold transition-all duration-300 transform hover:scale-105 artistic-border"
            >
              <span className="relative z-10 flex items-center">
                View All Challenges
                <ArrowRightIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 rounded-xl"></div>
            </Link>
          </div>

          {/* Bottom Artistic Element */}
          <div className="mt-16 text-center">
            <div className="inline-flex items-center space-x-3">
              <div className="w-12 h-0.5 bg-gradient-gold animate-gold-shimmer"></div>
              <ImageWithFallback
                src="/images/zova_labs_right.png"
                alt="ZovaLabs Logo"
                className="w-6 h-6 object-contain"
              />
              <div className="w-12 h-0.5 bg-gradient-gold animate-gold-shimmer" style={{animationDelay: '0.5s'}}></div>
            </div>
          </div>
        </div>
      </div>

      {/* Artistic Testimonials Section */}
      <div className="relative py-24 bg-dark-900 particle-bg">
        <div className="absolute inset-0 golden-mesh opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Section Header */}
          <div className="text-center mb-20">
            <div className="inline-flex items-center glass-morphism px-6 py-3 rounded-full mb-6">
              <UserGroupIcon className="w-5 h-5 text-gold-400 mr-2 animate-sparkle" />
              <span className="text-sm font-medium text-gray-300">Community Voices</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-display font-bold text-white mb-6">
              What our
              <span className="block holographic-text animate-holographic-shift">community says</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Hear from researchers, students, and industry professionals who have participated in our challenges
            </p>
          </div>

          {/* Testimonials Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index} 
                className="group relative"
                style={{animationDelay: `${index * 0.2}s`}}
              >
                {/* Testimonial Card */}
                <div className="luxury-card p-8 rounded-3xl hover:scale-105 transition-all duration-500 perspective-card floating-elements h-full flex flex-col">
                  
                  {/* Quote Icon */}
                  <div className="mb-6">
                    <div className="w-12 h-12 neo-morphism rounded-xl flex items-center justify-center group-hover:animate-gold-pulse">
                      <svg className="w-6 h-6 text-gold-400" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h4v10h-10z"/>
                      </svg>
                    </div>
                  </div>

                  {/* Testimonial Content */}
                  <div className="flex-1 mb-8">
                    <p className="text-gray-300 leading-relaxed text-lg group-hover:text-gray-200 transition-colors duration-300">
                      "{testimonial.content}"
                    </p>
                  </div>

                  {/* Author Section */}
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <ImageWithFallback
                        src={testimonial.avatar}
                        alt={testimonial.author}
                        className="h-14 w-14 rounded-full object-cover ring-2 ring-gold-500/30 group-hover:ring-gold-400/50 transition-all duration-300"
                        fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='currentColor'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' /%3E%3C/svg%3E"
                      />
                      <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-gold-400 rounded-full animate-pulse"></div>
                    </div>
                    <div>
                      <h4 className="font-display font-semibold text-white group-hover:text-gold-400 transition-colors duration-300">
                        {testimonial.author}
                      </h4>
                      <p className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors duration-300">
                        {testimonial.role}
                      </p>
                      <p className="text-sm text-gold-400 font-medium">
                        {testimonial.company}
                      </p>
                    </div>
                  </div>

                  {/* Decorative Elements */}
                  <div className="absolute top-4 right-4 w-2 h-2 bg-gold-400 rounded-full animate-sparkle" style={{animationDelay: `${index * 0.5}s`}}></div>
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-gold opacity-0 group-hover:opacity-60 transition-opacity duration-300"></div>
                </div>

                {/* Floating Decorative Elements */}
                <div className="absolute -top-3 -right-3 w-5 h-5 bg-gold-400/20 rounded-full animate-float" style={{animationDelay: `${index * 0.7}s`}}></div>
                <div className="absolute -bottom-3 -left-3 w-3 h-3 bg-gold-500/30 rounded-full animate-pulse" style={{animationDelay: `${index * 0.9}s`}}></div>
              </div>
            ))}
          </div>

          {/* Bottom Artistic Element */}
          <div className="mt-20 text-center">
            <div className="inline-flex items-center space-x-4">
              <div className="w-8 h-0.5 bg-gradient-gold animate-gold-shimmer"></div>
              <div className="flex space-x-2">
                <div className="w-2 h-2 bg-gold-400 rounded-full animate-pulse"></div>
                <div className="w-2 h-2 bg-gold-500 rounded-full animate-pulse" style={{animationDelay: '0.5s'}}></div>
                <div className="w-2 h-2 bg-gold-600 rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
              </div>
              <div className="w-8 h-0.5 bg-gradient-gold animate-gold-shimmer" style={{animationDelay: '0.5s'}}></div>
            </div>
          </div>
        </div>
      </div>

      {/* Revolutionary CTA Section */}
      <div className="relative py-32 aurora-bg overflow-hidden">
        <div className="absolute inset-0 golden-mesh opacity-20"></div>
        
        {/* Floating Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-gold-400/10 rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-gold-500/10 rounded-full blur-3xl animate-float" style={{animationDelay: '2s'}}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gold-300/5 rounded-full blur-3xl animate-pulse"></div>
        </div>

        <div className="relative max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          
          {/* Main CTA Content */}
          <div className="luxury-card p-12 rounded-3xl floating-elements">
            
            {/* Icon */}
            <div className="mb-8">
              <div className="w-20 h-20 mx-auto neo-morphism rounded-3xl flex items-center justify-center animate-gold-pulse">
                <RocketLaunchIcon className="w-10 h-10 text-gold-400" />
              </div>
            </div>

            {/* Heading */}
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-white mb-6">
              Ready to
              <span className="block holographic-text animate-holographic-shift text-stroke-gold">
                innovate?
              </span>
            </h2>

            {/* Subheading */}
            <p className="text-xl sm:text-2xl text-gray-300 mb-4">
              Start your AI journey today
            </p>
            
            {/* Description */}
            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-12">
              Join thousands of innovators building the future with AI. 
              Collaborate, learn, and make a real impact in the world of technology innovation.
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Link
                to="/register"
                className="group relative inline-flex items-center justify-center px-10 py-4 text-lg font-semibold text-white bg-gradient-gold rounded-xl shadow-2xl hover:shadow-gold transition-all duration-300 transform hover:scale-105 artistic-border"
              >
                <RocketLaunchIcon className="mr-3 h-6 w-6 group-hover:animate-bounce" />
                <span className="relative z-10">Get Started</span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 rounded-xl"></div>
              </Link>
              
              <Link
                to="/challenges"
                className="group relative inline-flex items-center justify-center px-10 py-4 text-lg font-semibold text-gold-400 glass-morphism rounded-xl hover:bg-gold-500/10 transition-all duration-300"
              >
                <AcademicCapIcon className="mr-3 h-6 w-6 group-hover:scale-110 transition-transform duration-200" />
                Explore Challenges
                <div className="absolute inset-0 rounded-xl bg-gradient-gold opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="mt-12 pt-8 border-t border-gold-500/20">
              <div className="flex flex-wrap justify-center items-center gap-8 text-sm text-gray-400">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-gold-400 rounded-full mr-2 animate-pulse"></div>
                  <span>10,000+ Active Participants</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-gold-400 rounded-full mr-2 animate-pulse" style={{animationDelay: '0.5s'}}></div>
                  <span>50+ Countries Represented</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-gold-400 rounded-full mr-2 animate-pulse" style={{animationDelay: '1s'}}></div>
                  <span>$500K+ Total Prizes</span>
                </div>
              </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute top-6 right-6 w-3 h-3 bg-gold-400 rounded-full animate-sparkle"></div>
            <div className="absolute bottom-6 left-6 w-2 h-2 bg-gold-500 rounded-full animate-pulse"></div>
          </div>

          {/* Bottom Artistic Element */}
          <div className="mt-16 text-center">
            <div className="inline-flex items-center space-x-4">
              <div className="w-16 h-0.5 bg-gradient-gold animate-gold-shimmer"></div>
              <ImageWithFallback
                src="/images/zova_labs_right.png"
                alt="ZovaLabs Logo"
                className="w-8 h-8 object-contain"
              />
              <div className="w-16 h-0.5 bg-gradient-gold animate-gold-shimmer" style={{animationDelay: '0.5s'}}></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}